import { ChangeEvent, FC, useCallback, useEffect, useState } from 'react'
import Big from 'big.js'
import { useDispatch, useSelector } from 'react-redux'
import useDynamicMaster from '../../../hooks/useDynamicMaster'
import {
  selectInvestAmountForCheckout,
  selectIsCheckoutScreen,
} from '../../../modules/miscUi/selectors'
import {
  selectAccount,
  selectBalance,
  selectBalanceEth,
  selectIsEthRaise,
} from '../../../modules/web3/selectors'
import { Box } from '../../../shared_components/UI/Box'
import { IPool } from '../../../types/reducerTypes'
import { amountFromWei, amountToWei } from '../../../utils/amount'
import { numberWithCommas } from '../../../utils/commaSeperators'
import { Title } from '../../Home/parts'
import { PoolInput } from '../parts'
import AnimatedLoader from '../../../shared_components/AnimatedLoader'
import useWeb3 from '../../../hooks/useWeb3'
import { isWithinAllowance } from '../../../hooks/useMasterContract'
import useMultiStepTx from '../../../hooks/useMultiStepTx'
import {
  Container,
  ContentHolder,
  FullWidthSeperator,
  Inner,
  InputContainer,
  Section,
  SectionTitle,
  Table,
  TableLabel,
  TableRow,
  TopTableContainer,
} from './styles'
import FAQ from './FAQ'
import PaymentMethods from './PaymentMethods'
import LegalStuff from './LegalStuff'
import InvestmentCompleted from './InvestmentCompleted'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { Icon, TextAndIcon, WarningText } from '../../../shared_components/Page'
import { setIsCheckoutScreen } from '../../../modules/miscUi/actions'
import { toast } from 'react-toastify'
import { USDB_DECIMALS } from '../../../config'
import { ThemedButton } from '../../../shared_components/ThemedButton'
import { theseus } from '../../../api/theseus'
import { numberToLocaleString } from '../../../utils/numbers'
import { showDecimalsIfNeeded } from '../../Dashboard/Allocations/InvestmentInfo'
import { useGetContractByNameQuery } from '../../../modules/web3/api'
import { createContract } from '../../Dashboard/Launches'
import useNetworkId from '../../../hooks/useNetworkId'

interface Props {
  pool: IPool
  calculatedMaxAllocation: number
  calculatedTaxFreeAllocation: number
  whitelistAllocation: number
  taxRate: number
  previousInvestment: number
}

const Checkout: FC<Props> = ({
  pool,
  calculatedMaxAllocation,
  calculatedTaxFreeAllocation,

  previousInvestment,
}) => {
  const investAmountSelector = useSelector(selectInvestAmountForCheckout)
  const account = useSelector(selectAccount)
  const balance = useSelector(selectBalance)
  const balanceEth = useSelector(selectBalanceEth)
  const isCheckout = useSelector(selectIsCheckoutScreen)

  const history = useHistory()
  const { url } = useRouteMatch()
  const initialInvestAmount = history?.location?.search?.split('=')[1]

  const {
    updateBalance,
    provider,
    // contracts: { busd },
  } = useWeb3()
  const { setTotalSteps, nextStepCallback } = useMultiStepTx()

  const isLabsPool = !!pool?.isVetted

  const isEthRaise = useSelector(selectIsEthRaise)

  const currency = isEthRaise ? 'ETH' : 'USDB'

  const contractName = isLabsPool
    ? undefined
    : `TokenSale${isEthRaise ? 'ETH' : 'USDB'}`

  const dynamicMaster = useDynamicMaster(
    pool?.poolAddress,
    undefined,
    contractName,
  )

  const dynamicMasterReady = !!dynamicMaster

  const dispatch = useDispatch()

  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false)
  const [isInvesting, setIsInvesting] = useState(false)

  const [investAmount, setInvestAmount] = useState(
    Number(initialInvestAmount) ?? investAmountSelector,
  )

  const [estimatedTokens, setEstimatedTokens] = useState(0)
  const [estimatedRefund, setEstimatedRefund] = useState(0)
  const [isFiat, setIsFiat] = useState(false)
  const [hasInvested, setHasInvested] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [hasSetFromCache, setHasSetFromCache] = useState(false)
  const [isKyc, setIsKyc] = useState(false)
  const [hasPassedKYC, setHasPassedKYC] = useState(false)
  const [gasEstimate, setGasEstimate] = useState(0)
  const [insufficientBalanceForGas, setInsufficientBalanceForGas] =
    useState(false)

  const { networkId } = useNetworkId()
  const { data: tokenSaleUsdbContractData, error: tokenSaleUsdbContractError } =
    useGetContractByNameQuery({
      name: `TokenSale${isEthRaise ? 'ETH' : 'USDB'}`,
      chainId: networkId,
    })

  const tokenSaleAbi = tokenSaleUsdbContractData?.contract?.abi

  useEffect(() => {
    if (!tokenSaleUsdbContractError) return
    console.error('Error fetching contract data', tokenSaleUsdbContractError)
  }, [tokenSaleUsdbContractError])

  const kycValidation = !isKyc || hasPassedKYC

  const taxRate = pool?.poolSettings?.taxRate ?? 0

  const handleInvestChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!calculatedMaxAllocation) return
      const amount = Number(event.target.value)

      if (amount < calculatedMaxAllocation - previousInvestment) {
        setInvestAmount(amount)
        return
      }

      setInvestAmount(calculatedMaxAllocation - previousInvestment)
    },
    [calculatedMaxAllocation, previousInvestment],
  )

  const totalToPay = Math.round(parseInt(`${investAmount}`, 10))
  const totalTaxToPay = Math.round(
    parseInt(`${investAmount * ((taxRate ?? 0) / 100)}`, 10),
  )
  const totalToPayWithTaxAndGas =
    investAmount +
    investAmount * ((taxRate ?? 0) / 100) +
    amountFromWei(new Big(gasEstimate), 9).toNumber()

  const invest = useCallback(async () => {
    try {
      if (!tokenSaleAbi) throw new Error('No token sale abi')
      const { poolSettings } = pool ?? {}

      if (!poolSettings) throw new Error('No pool settings')
      if (!dynamicMaster) return
      if (isInvesting) return

      const availableAmount = amountFromWei(
        isEthRaise ? balanceEth : balance,
        isEthRaise ? 18 : USDB_DECIMALS,
      ).toNumber()
      const isTotalMoreThanAvailable = totalToPay > availableAmount

      if (isTotalMoreThanAvailable) {
        toast.error('Total to pay exceeds your balance')
        return
      }

      setIsInvesting(true)

      if (hasError) setHasError(false)

      // const allowance = await busd.allowance(account, pool.poolAddress)
      const allowance = '0'

      const amountToAllow = investAmount

      const amount = isLabsPool
        ? investAmount
        : amountToWei(investAmount, USDB_DECIMALS)

      if (
        !isEthRaise &&
        !isWithinAllowance(allowance, amountToWei(amountToAllow).toString())
      ) {
        setTotalSteps(2)
        // await busd.approve(
        //   pool.poolAddress,
        //   isLabsPool
        //     ? investAmount + investAmount * (taxRate / 100)
        //     : investAmount,
        // )
      } else {
        setTotalSteps(1)
      }

      const contract = isLabsPool
        ? dynamicMaster
        : createContract(tokenSaleAbi, pool.poolAddress, provider)

      const args = isEthRaise ? [] : [amount.toString()]

      const gas = await contract?.deposit.estimateGas(...args, {
        value: isEthRaise ? amount.toString() : undefined,
        from: account,
      })

      let error = null

      const tx = await contract
        ?.deposit(
          ...args,
          {
            gas,
            value: isEthRaise ? amount.toString() : undefined,
            from: account,
          },
          nextStepCallback,
        )
        .catch((err: any) => (error = err))

      setIsInvesting(false)

      const txHash = tx?.transactionHash

      if (error) {
        setHasError(true)
        return
      }
      updateBalance(account)
      await theseus.post(`/pools/${pool.poolAddress}/user-invested`, {
        txHash,
      })
      setHasInvested(true)
    } catch (e) {
      console.error('error on invest', e)
      toast.error('There was an error, please check your wallet and try again.')
    } finally {
      setIsInvesting(false)
    }
  }, [
    account,
    balance,
    dynamicMaster,
    hasError,
    investAmount,
    isInvesting,
    nextStepCallback,
    setTotalSteps,
    totalToPay,
    updateBalance,
    isLabsPool,
    pool,
    isEthRaise,
    tokenSaleAbi,
    provider,
    balanceEth,
  ])

  const fetchGasEstimate = useCallback(async () => {
    try {
      const contract = createContract(tokenSaleAbi, pool.poolAddress, provider)

      const amount = amountToWei(investAmount - 0.02, USDB_DECIMALS)

      const args = isEthRaise ? [] : [amount.toString()]

      const gas = await contract?.deposit.estimateGas(...args, {
        value: isEthRaise ? amount.toString() : undefined,
        from: account,
      })

      setGasEstimate(Number(gas * 6n))

      const canAffordGas = new Big(balanceEth).gt(
        new Big(Number(gas * 6n)).plus(amountToWei(investAmount, 18)),
      )

      setInsufficientBalanceForGas(!canAffordGas)
    } catch (e) {
      console.error('error on fetchGasEstimate', e.message)
    }
  }, [
    account,
    balanceEth,
    investAmount,
    isEthRaise,
    pool,
    tokenSaleAbi,
    provider,
  ])

  useEffect(() => {
    if (
      !isEthRaise ||
      !provider ||
      !account ||
      !tokenSaleAbi
      // ||
      // hasInvested ||
      // isInvesting
    )
      return
    fetchGasEstimate()
  }, [
    isEthRaise,
    account,
    pool,
    hasInvested,
    isInvesting,
    tokenSaleAbi,
    provider,
    investAmount,
    balanceEth,
    fetchGasEstimate,
  ])

  const calculateClaimEstimates = useCallback(async () => {
    try {
      const state = isLabsPool
        ? await dynamicMaster?.getState()
        : await createContract(
            tokenSaleAbi,
            pool?.poolAddress,
            provider,
          ).state()
      const totalSold = state[0]
      const supply =
        pool?.totalSaleSupply ?? pool?.poolSettings?.totalSupplyForSale
      const soldInUSDB = amountFromWei(new Big(totalSold), USDB_DECIMALS)
        .div(pool?.privateSale?.price)
        .toString()

      const tokenPrice = new Big(pool.privateSale.price).toNumber()

      if (investAmount > 0 && supply && totalSold && pool?.privateSale?.price) {
        const ratio =
          totalSold !== '0'
            ? Math.min(new Big(supply).div(soldInUSDB).toNumber(), 1)
            : 1

        const isOversold = ratio !== 1 && ratio < 1

        setEstimatedTokens((investAmount / tokenPrice) * ratio)

        if (isOversold) {
          setEstimatedRefund(
            new Big(investAmount)
              .minus(new Big(new Big(investAmount).times(new Big(ratio))))
              .toNumber(),
          )
        }
        return
      }

      setEstimatedTokens(investAmount / tokenPrice)
    } catch (e) {
      console.error('error on calculateClaimEstimates', e)
    }
  }, [investAmount, pool, dynamicMaster, provider])

  useEffect(() => {
    return () => {
      dispatch(setIsCheckoutScreen(false))
      localStorage.removeItem('checkout')
    }
  }, [dispatch])

  useEffect(() => {
    if (investAmount) {
      calculateClaimEstimates()
    }
  }, [investAmount, calculateClaimEstimates])

  useEffect(() => {
    const cachedCheckout = localStorage.getItem('checkout')

    if (isCheckout) {
      localStorage.setItem(
        'checkout',
        JSON.stringify({
          poolAddress: pool.poolAddress,
          investAmount: investAmount,
        }),
      )
      return
    }

    if (
      !isCheckout &&
      (!cachedCheckout || Object.keys(JSON.parse(cachedCheckout)).length === 0)
    ) {
      history.push(url.split('/checkout')[0] + '/about')
      return
    }

    if (hasInvested) {
      history.push(url + '/complete')
      return
    }
  }, [
    isCheckout,
    history,
    url,
    hasInvested,
    investAmount,
    pool,
    dispatch,
    hasSetFromCache,
  ])

  const checkIsKyc = useCallback(async () => {
    try {
      if (!dynamicMaster) return
      const isKycPool = await dynamicMaster?.isKYCEnabled()
      setIsKyc(isKycPool)
    } catch (e) {
      console.error('error on checkIsKyc', e)
    }
  }, [dynamicMaster])

  useEffect(() => {
    if (!dynamicMasterReady) return
    checkIsKyc()
  }, [dynamicMasterReady, checkIsKyc])

  const taxToPay = investAmount * (taxRate / 100)

  function reduceInvestmentToAllowForGas() {
    if (!gasEstimate) return
    const gasAmount = amountFromWei(new Big(gasEstimate), 9).toNumber()
    const newInvestAmount = investAmount - gasAmount
    setInvestAmount(newInvestAmount)
  }

  return (
    <Container>
      {!hasInvested && <Title maxWidth={737}>Confirm Token Purchase</Title>}
      <Inner>
        <ContentHolder direction="column">
          {hasInvested ? (
            <InvestmentCompleted
              pool={pool}
              investAmount={investAmount}
              isLabsPool={isLabsPool}
            />
          ) : (
            <>
              <Section>
                <SectionTitle>Token Purchase summary</SectionTitle>
                <TopTableContainer>
                  <Table direction="column">
                    <strong>What you'll pay</strong>
                    <TableRow className="mobile-col">
                      <Box direction="column" style={{ paddingRight: 8 }}>
                        <TableLabel>Purchase amount</TableLabel>
                        <span className="notice">
                          {Boolean(previousInvestment)
                            ? 'REMAINING'
                            : 'MAX ALLOWED'}
                          : {calculatedMaxAllocation - previousInvestment}{' '}
                          {currency}
                        </span>
                      </Box>
                      <InputContainer>
                        <PoolInput
                          isSmall
                          type="number"
                          onWheel={(e) => e.preventDefault()}
                          value={investAmount}
                          onChange={handleInvestChange}
                          placeholder=""
                          max={calculatedMaxAllocation - previousInvestment}
                          min={0}
                        />
                        <span>{currency}</span>
                      </InputContainer>
                    </TableRow>
                    {isLabsPool && !!taxToPay && (
                      <TableRow>
                        <TableLabel>
                          <strong className="label">Tax</strong>
                        </TableLabel>
                        <TableLabel>
                          <span className="total">
                            {numberToLocaleString(totalTaxToPay)}
                          </span>{' '}
                          &nbsp;{currency}
                        </TableLabel>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableLabel>
                        <strong className="label">Total</strong>
                      </TableLabel>
                      <TableLabel>
                        <span className="total">
                          {showDecimalsIfNeeded(
                            totalToPayWithTaxAndGas,
                            isEthRaise ? 5 : 2,
                          )}
                        </span>{' '}
                        &nbsp;{currency}
                      </TableLabel>
                    </TableRow>
                  </Table>
                  <Table direction="column">
                    <strong>What you'll get</strong>
                    <TableRow>
                      <TableLabel>Estimated Tokens</TableLabel>
                      <TableLabel>
                        {estimatedTokens
                          ? numberWithCommas(
                              parseInt(estimatedTokens.toString()),
                            )
                          : '-'}
                      </TableLabel>
                    </TableRow>
                    <TableRow>
                      <TableLabel>Estimated Refund</TableLabel>
                      <TableLabel>
                        {estimatedRefund
                          ? isEthRaise
                            ? estimatedRefund
                            : numberWithCommas(
                                parseInt(String(estimatedRefund)),
                              )
                          : '-'}{' '}
                        {currency}
                      </TableLabel>
                    </TableRow>
                  </Table>
                </TopTableContainer>
              </Section>
              <FullWidthSeperator />
              {/* {isKyc && (
                <KYC
                  hasPassedKYC={hasPassedKYC}
                  setHasPassedKYC={setHasPassedKYC}
                />
              )} */}
              <Section>
                <SectionTitle>Payment Method</SectionTitle>
                <PaymentMethods isFiat={isFiat} setIsFiat={setIsFiat} />
              </Section>
              <FullWidthSeperator />
              <Section>
                <SectionTitle>Legal Stuff</SectionTitle>
                <LegalStuff
                  hasAcceptedTerms={hasAcceptedTerms}
                  setHasAcceptedTerms={setHasAcceptedTerms}
                  isLaunchPool={!isLabsPool}
                />
              </Section>
              <FullWidthSeperator />
              <Section noBorder>
                <SectionTitle>Complete Token Purchase</SectionTitle>{' '}
                <Table direction="column">
                  <TableRow>
                    <TableLabel>Your token purchase</TableLabel>
                    <TableLabel>
                      {investAmount ? numberWithCommas(investAmount) : '-'}{' '}
                      {currency}
                    </TableLabel>
                  </TableRow>
                  {!!gasEstimate && (
                    <>
                      <TableRow>
                        <TableLabel>Estimated Gas</TableLabel>
                        <TableLabel>
                          {amountFromWei(new Big(gasEstimate), 9).toString()}{' '}
                          {currency}
                        </TableLabel>
                      </TableRow>
                      {insufficientBalanceForGas && (
                        <TableRow>
                          <TableLabel
                            pseudoLink
                            onClick={reduceInvestmentToAllowForGas}
                          >
                            <Icon
                              src="/img/icons/chain-warning.svg"
                              alt="chain warning"
                            />
                            <WarningText>
                              Insufficient balance for gas! Click here to reduce
                              investment.
                              <br /> Note gas prices may change.
                            </WarningText>
                          </TableLabel>
                        </TableRow>
                      )}
                    </>
                  )}
                  {isLabsPool && !!taxRate && (
                    <TableRow>
                      <TableLabel>Tax</TableLabel>
                      <TableLabel>
                        {showDecimalsIfNeeded(
                          totalTaxToPay,
                          isEthRaise ? 5 : 2,
                        )}{' '}
                        {currency}
                      </TableLabel>
                    </TableRow>
                  )}
                  <TableRow noBorder>
                    <TableLabel>
                      <strong className="label">Total to pay</strong>
                    </TableLabel>
                    <TableLabel>
                      <span className="total">
                        {showDecimalsIfNeeded(
                          totalToPayWithTaxAndGas,
                          isEthRaise ? 8 : 2,
                        )}{' '}
                      </span>{' '}
                      &nbsp;{currency}
                    </TableLabel>
                  </TableRow>
                  <Box marginTop={16} direction="column">
                    <ThemedButton
                      onClick={invest}
                      w="280px"
                      p="0"
                      $styleType="primary"
                      disabled={
                        !kycValidation ||
                        !hasAcceptedTerms ||
                        account === '' ||
                        investAmount === 0 ||
                        insufficientBalanceForGas
                      }
                    >
                      {isInvesting ? (
                        <AnimatedLoader size={18} />
                      ) : (
                        'Complete Token Purchase'
                      )}
                    </ThemedButton>
                    {hasError && (
                      <Box marginTop={32}>
                        <TextAndIcon>
                          {' '}
                          <Icon
                            src="/img/icons/chain-warning.svg"
                            alt="chain warning"
                          />
                          <WarningText>
                            There was an error, please check your wallet and try
                            again.
                          </WarningText>
                        </TextAndIcon>
                      </Box>
                    )}
                  </Box>
                </Table>
              </Section>
            </>
          )}
        </ContentHolder>
        {!hasInvested && <FAQ />}
      </Inner>
    </Container>
  )
}

export default Checkout
